import { VALIDATE_REQUIRED } from '@/config';
import { useTranslation } from 'react-i18next';
import { Form, Input, message, Modal, Tooltip } from 'antd';
import React, { ReactElement } from 'react';
import { SaveOutlined, QuestionOutlined } from '@ant-design/icons';
import { apiSdk } from '@/httpClient';

interface Props {
  onClose: () => void;
}

export default function ChangePasswordModal({ onClose }: Props): ReactElement {
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [passwordValidationMessages, setPasswordValidationMessages] = React.useState<string[]>([]);
  React.useEffect(() => {
    apiSdk.PasswordPolicyService.getValidationMessages().then(res => {
      setPasswordValidationMessages(res);
    });
  }, []);
  const handleOk = () => {
    form
      .validateFields()
      .then(v => {
        setLoading(true);
        apiSdk.UserService.changePassword({
          body: { oldPassword: v.oldpassword, password: v.password },
        })
          .then(res => {
            if (res.success) {
              message.success(t('updatePassword_ChangePasswordSuccess'));
              onClose();
            } else {
              message.error(t(res.message || ''));
            }
          })
          .catch(err => {
            message.error(err);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch();
  };
  return (
    <Modal
      visible={true}
      maskClosable={false}
      width={400}
      className="nta-custom-form"
      title={t('updatePassword_Title')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={onClose}
      onOk={handleOk}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="oldpassword" label={t('updatePassword_CurrentPassword')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
          <Input.Password className="w-100" />
        </Form.Item>

        <Form.Item name="password" label={t('updatePassword_NewPassword')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
          <Input.Password
            className="w-100"
            addonAfter={
              <Tooltip title={<div dangerouslySetInnerHTML={{ __html: passwordValidationMessages.join('<br/>') }} />}>
                <QuestionOutlined />
              </Tooltip>
            }
          />
        </Form.Item>
        <Form.Item
          name="repassword"
          label={t('updatePassword_ConfirmPassword')}
          rules={[
            { required: true, message: VALIDATE_REQUIRED },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(t('updatePassword_PasswordNotMatch'));
              },
            }),
          ]}
        >
          <Input.Password
            className="w-100"
            addonAfter={
              <Tooltip title={<div dangerouslySetInnerHTML={{ __html: passwordValidationMessages.join('<br/>') }} />}>
                <QuestionOutlined />
              </Tooltip>
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
